import React, { useEffect, useLayoutEffect, useRef, useState, Suspense } from 'react';
import '../../../styles/newsletter-signup.css';
import '../../askGraspd/components/AskGraspdPopup.css'; // Load highlight styles early
import MarkdownView from './MarkdownView';
import QuizView from './QuizView';
import KnowledgeCheckView from './KnowledgeCheckView';
import { useTextSelection } from '../../askGraspd/hooks/useTextSelection';
import { AskGraspdPopup } from '../../askGraspd/components/AskGraspdPopup';
import { useAskGraspdHistory } from '../../askGraspd/hooks/useAskGraspdHistory';
import { SavedHighlightsWrapper } from '../../askGraspd/components/SavedHighlightsWrapper';
import { CourseMasteryProgress } from '../../adaptive/components/CourseMasteryProgress';
import { useAdaptiveV2 } from '../../adaptive/hooks/useAdaptiveV2';
import { previousSectionOf } from '../utils/sectionNavigation';
import { adaptiveApi } from '../../adaptive/services/adaptiveApi';
import { Share2 } from 'lucide-react';
import ShareModal from '../../courseShare/components/ShareModal';
import TopbarActions from '../../../layout/TopbarActions';
import { useCourseProgress } from '../hooks/useCourseProgress';
import CourseOutlineRail from './CourseOutlineRail';
import CourseOutlineTree from './CourseOutlineTree';
import LessonFooter from './LessonFooter';
import { Skeleton, SkeletonText, SkeletonRegion } from '../../../components/ui/Skeleton';
import { extractLeadingHeading } from '../utils/extractLeadingHeading';

const HtmlView = React.lazy(() => import('./HtmlView'));

export default function CourseLearnView(props) {
  const {
    outline, loadingOutline,
    lessonView, loadingLesson,
    selectedSectionId,
    status, ownershipWarning,
    onSelectNavPrev,
    onSelectNavNext,
    onClickLessonSection,
    quizState,
    knowledgeCheckState,
    currentSection, onCompleteSection, markingComplete,
    localViewed, // Set of locally marked section IDs for immediate UI feedback
    onKnowledgeCheckComplete, // Called when knowledge check is passed (backend confirmed)
    courseId, // For Ask Graspd
    lessonId, // For Ask Graspd
    courseName, // For the Share dialog + rail header
  } = props;

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // The outline endpoint names this `courseTitle` (CourseOutlineResponse), not
  // `title` — reading `outline.title` is why the rail header rendered blank.
  // lessonView.course.courseTitle is the same string from the lesson payload
  // and covers the window before the outline resolves; `courseName` is the
  // title the dashboard/search stashed in sessionStorage on the way in.
  const courseTitle = outline?.courseTitle
    || lessonView?.course?.courseTitle
    || courseName
    || 'Course';
  const [renderLessonView, setRenderLessonView] = useState(lessonView);

  // V2 Adaptive Learning - mastery progress for this course
  const { v2Enabled } = useAdaptiveV2();
  const [courseMastery, setCourseMastery] = useState(null);

  // Fetch mastery params when V2 is enabled and we have a courseId (used as topicId)
  useEffect(() => {
    if (!v2Enabled || !courseId) {
      setCourseMastery(null);
      return;
    }

    let cancelled = false;
    adaptiveApi.getParams({ topicId: courseId, auth: true })
      .then(data => {
        if (!cancelled && data.v2Enabled) {
          setCourseMastery(data);
        }
      })
      .catch(() => {
        // Silently fail - mastery display is optional
        if (!cancelled) setCourseMastery(null);
      });

    return () => { cancelled = true; };
  }, [v2Enabled, courseId]);

  const railRef = useRef(null);        // outline rail scroll container
  const bodyRef = useRef(null);        // main content scroll container
  const savedRailScroll = useRef(null);
  const sectionButtonRefs = useRef({});  // refs for section buttons to scroll into view
  const hasScrolledToSection = useRef(false);  // track if we've done initial scroll
  const contentRef = useRef(null);     // content area for Ask Graspd text selection

  // Derive the active section item from the rendered lesson view (moved up for useTextSelection)
  const activeItem = React.useMemo(() => {
    const items = renderLessonView?.sections || [];
    return items.find(s => (s?.section?.sectionId ?? s?.sectionId) === selectedSectionId) || null;
  }, [renderLessonView?.sections, selectedSectionId]);

  // Rail header shows the CURRENT LESSON's title, not the course title (the
  // course title still appears in the topbar breadcrumb, so this isn't a
  // duplication concern). Prefer the leading heading in the active section's
  // HTML content, then the section/lesson-view title, then fall back to the
  // course-title chain above. Also guards against `outline.courseTitle`
  // literally being the backend's unresolved placeholder string.
  const lessonTitle = React.useMemo(() => {
    const candidates = [
      extractLeadingHeading(activeItem?.content?.html),
      activeItem?.section?.title,
      renderLessonView?.title,
      courseTitle,
    ];
    return candidates.find((c) => c && c !== '(title loaded in lesson view)') || 'Course';
  }, [activeItem?.content?.html, activeItem?.section?.title, renderLessonView?.title, courseTitle]);

  // Compute content type early so we can pass it to useTextSelection
  // Normalize to uppercase since content.type uses lowercase ('quiz', 'knowledge_check')
  // but section.contentType uses uppercase ('QUIZ', 'KNOWLEDGE_CHECK')
  const effectiveContentType = React.useMemo(() => {
    const raw = currentSection?.contentType
      ?? activeItem?.section?.contentType
      ?? activeItem?.content?.type
      ?? null;
    if (!raw) return null;
    // Normalize: 'knowledge_check' -> 'KNOWLEDGE_CHECK', 'quiz' -> 'QUIZ', etc.
    return raw.toUpperCase().replace('_', '_');
  }, [currentSection?.contentType, activeItem?.section?.contentType, activeItem?.content?.type]);

  // Text selection for Ask Graspd - uses different strategies for HTML vs Markdown
  const { selection, clearSelection, highlightRects } = useTextSelection(contentRef, {
    contentType: effectiveContentType,
  });

  // Fetch Ask Graspd history for the current section (for rendering saved highlights)
  const { history: askGraspdHistory, addToHistory: addAskGraspdToHistory } = useAskGraspdHistory(selectedSectionId);

  // Fallback highlight overlay for browsers without CSS Custom Highlight API
  const HighlightOverlay = highlightRects && highlightRects.length > 0 ? (
    <div className="selection-highlight-overlay" aria-hidden="true">
      {highlightRects.map((rect, index) => (
        <div
          key={index}
          className="selection-highlight-rect"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
        />
      ))}
    </div>
  ) : null;

  useEffect(() => {
    if (!loadingLesson && lessonView) {
      setRenderLessonView(lessonView);
    }
  }, [loadingLesson, lessonView]);

  // Scroll outline to show selected section on initial load
  useEffect(() => {
    if (!loadingOutline && outline && selectedSectionId && !hasScrolledToSection.current) {
      // Small delay to ensure buttons are rendered
      const timer = setTimeout(() => {
        const buttonEl = sectionButtonRefs.current[selectedSectionId];
        if (buttonEl && railRef.current) {
          buttonEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          hasScrolledToSection.current = true;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loadingOutline, outline, selectedSectionId]);

  useLayoutEffect(() => {
    if (savedRailScroll.current != null && railRef.current) {
      railRef.current.scrollTop = savedRailScroll.current;
    }
  }, [selectedSectionId]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedSectionId]);

  // Fallback viewed flag from outline (in case lessonView/currentSection don't carry it through)
  const outlineViewed = React.useMemo(() => {
    if (!outline || !Array.isArray(outline.lessons)) return false;
    for (const lesson of outline.lessons) {
      const secs = lesson?.sections || [];
      for (const sec of secs) {
        if (sec?.sectionId === selectedSectionId && sec.viewed) {
          return true;
        }
      }
    }
    return false;
  }, [outline, selectedSectionId]);

  // Progress + prev/next derivation — single source of truth for the rail
  // header, the sticky footer, and "next section" navigation. Previously
  // there were three competing sources (lessonView.navigation,
  // useSectionView().navigation which was fetched and discarded, and a
  // client-side linear scan here). This hook replaces the scan; the other
  // two were already dead/unused for driving UI.
  const progress = useCourseProgress({ outline, selectedSectionId, localViewed });

  // Kept as `nextSectionInfo` with its old { lessonId, sectionId, sameLesson }
  // shape so every existing call site below (QuizView, KnowledgeCheckView,
  // the mark-complete flow) needs no further changes — only the source of
  // the data changed.
  const nextSectionInfo = React.useMemo(() => {
    const next = progress.nextSection;
    if (!next) return null;
    return {
      lessonId: next.moduleId,
      sectionId: next.sectionId,
      sameLesson: next.moduleId === lessonView?.lesson?.lessonId,
    };
  }, [progress.nextSection, lessonView?.lesson?.lessonId]);

  // "Previous" has its own existing source (onSelectNavPrev / goPrev, driven
  // by lessonView.navigation server data) — not one of the three collapsed
  // above. Mirror the same nav flags here purely to decide the footer's
  // disabled state; the actual navigation still goes through onSelectNavPrev.
  // Enabled exactly when goPrev (useCourseLearn) can move: an earlier section in
  // the ordered outline, or — as a fallback before the outline loads — the
  // server-provided nav. Keeps the button's state in lockstep with navigation.
  const hasPrev = !!previousSectionOf(outline, selectedSectionId)
    || !!(lessonView?.navigation?.previousSectionId || lessonView?.navigation?.previousLessonId);

  // Check if we have valid content loaded (not in loading/error state)
  const hasValidContent = React.useMemo(() => {
    if (!renderLessonView?.sections) return false;
    const active = renderLessonView.sections.find(s => (s?.section?.sectionId || s?.sectionId) === selectedSectionId);
    if (!active) return false;
    // Has content if it's a quiz, knowledge check, HTML, or has markdown content
    // Note: content.type uses lowercase ('html', 'markdown') while section.contentType uses uppercase ('HTML', 'MARKDOWN')
    const contentType = (active?.content?.type || active?.section?.contentType || effectiveContentType || '').toUpperCase();
    return contentType === 'QUIZ' || contentType === 'KNOWLEDGE_CHECK' || contentType === 'HTML' || contentType === 'MARKDOWN' || !!active?.content?.markdown || !!active?.content?.html;
  }, [renderLessonView?.sections, selectedSectionId, effectiveContentType]);

  // Check if section is viewed from outline OR local state (for immediate feedback)
  const isViewed = outlineViewed || (localViewed && localViewed.has(selectedSectionId));
  // Only show as "completed/next" if viewed AND not currently marking
  // This prevents the "Next" flash during mark+navigate
  const displayViewed = isViewed && !markingComplete;


  // Close mobile nav when section is selected
  const handleMobileSectionClick = (lessonId, sectionId, currentLessonId) => {
    setMobileNavOpen(false);
    onClickLessonSection(lessonId, sectionId, currentLessonId);
  };

  // Desktop rail click: save scroll position before navigating so the
  // useLayoutEffect below can restore it (rail must not jump to the top).
  const handleRailSectionClick = (lessonId, sectionId, currentLessonId) => {
    if (railRef.current) savedRailScroll.current = railRef.current.scrollTop;
    onClickLessonSection(lessonId, sectionId, currentLessonId);
  };

  return (
    <>
      <style>{`
        /* Mobile nav toggle button - bottom right to avoid layout speed dial */
        .mobile-nav-toggle {
          display: none;
          position: fixed;
          bottom: 24px;
          right: 24px;
          width: 56px;
          height: 56px;
          border-radius: var(--radius-panel);
          background: var(--bg-elevated);
          border: 1px solid var(--border-subtle);
          color: var(--text-primary);
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 100;
          box-shadow: var(--shadow-md);
        }
        /* Mobile nav overlay */
        .mobile-nav-overlay {
          display: none;
          position: fixed;
          top: var(--topbar-h);
          left: 0;
          right: 0;
          bottom: 0;
          background: var(--overlay-bg);
          z-index: 998;
        }
        /* Subtle vignette shown over the lesson body while a new section is
           loading. Direction of the tint flips per theme so it reads as a
           soft shadow in both instead of vanishing (dark-on-dark) or
           inverting (light mode). */
        .lesson-loading-scrim {
          position: absolute;
          inset: 0;
          pointer-events: none;
          transition: opacity .2s ease;
        }
        [data-theme="dark"] .lesson-loading-scrim {
          background: linear-gradient(transparent, rgba(255,255,255,0.05) 30%, transparent);
        }
        [data-theme="light"] .lesson-loading-scrim {
          background: linear-gradient(transparent, rgba(0,0,0,0.05) 30%, transparent);
        }
        /* Mobile nav drawer */
        .mobile-nav-drawer {
          display: none;
          position: fixed;
          top: var(--topbar-h);
          left: 0;
          bottom: 0;
          width: min(85vw, 320px);
          background: var(--bg-primary);
          border-right: 1px solid var(--border-subtle);
          flex-direction: column;
          z-index: 999;
          transform: translateX(-100%);
          transition: transform 0.3s ease;
        }
        .mobile-nav-drawer.open {
          transform: translateX(0);
        }

        /* Below the shell's sidebar breakpoint the fixed sidebar is hidden
           (shell.css), so the reader reclaims the left edge instead of
           leaving a 236px gutter where the rail used to be. */
        @media (max-width: 1024px) {
          .course-learn-layout {
            left: 0 !important;
          }
        }

        /* Mobile styles for CourseLearnView */
        @media (max-width: 768px) {
          .course-learn-layout {
            left: 0 !important;
            grid-template-columns: 1fr !important;
          }
          .course-learn-layout .learn-rail-desktop {
            display: none !important;
          }
          .course-learn-layout .learn-body {
            padding: 24px 16px !important;
            padding-bottom: 100px !important; /* Clear speed dials */
          }
          .course-learn-layout .collapsed-rail-desktop {
            display: none !important;
          }
          /* Show mobile nav elements. Lifted above the sticky LessonFooter
             (64px tall, pinned to the same viewport bottom edge) so the two
             don't overlap. */
          .mobile-nav-toggle {
            display: flex;
            bottom: 80px;
          }
          .mobile-nav-overlay {
            display: block;
          }
          .mobile-nav-drawer {
            display: flex;
          }
        }
      `}</style>
    <div
      className="course-learn-layout"
      style={{
        position: 'fixed',
        top: 'var(--topbar-h)',
        left: 'var(--layout-sidebar-w)',
        right: 0,
        bottom: 0,
        display: 'grid',
        // Fixed 288px rail (mockup 1c, line 541) — a percentage made the
        // outline balloon on wide monitors and squeeze on small laptops.
        gridTemplateColumns: isCollapsed ? '48px 1fr' : '288px 1fr',
        transition: 'grid-template-columns 0.3s ease',
        overflow: 'hidden',
        boxSizing: 'border-box',
        margin: 0
      }}
    >
      {/* Lesson chrome in the app top bar (mockup 1c, lines 612-616): the
          position counter, then Share. Portalled into the shell's action
          slot — see TopbarActions.jsx. The mobile outline drawer keeps its
          own share button; this slot is hidden below the sidebar
          breakpoint. */}
      <TopbarActions>
        {progress.currentIndex != null && progress.totalSections > 0 && (
          <span className="topbar-action-meta">
            Lesson {progress.currentIndex} of {progress.totalSections}
          </span>
        )}
        <button
          type="button"
          className="topbar-action-btn"
          onClick={() => setShareOpen(true)}
          title="Share lesson"
        >
          <Share2 size={13} aria-hidden="true" />
          Share lesson
        </button>
      </TopbarActions>

      {/* Share Course dialog (renders via portal; null when closed). Keyed on
          courseId for the same reason as DashboardView's usage — see comment
          there. Not actually reachable here since this page is scoped to one
          course per mount, but keeps the two usages consistent. */}
      <ShareModal
        key={courseId || 'none'}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        courseId={courseId}
        courseTitle={courseTitle}
      />

      {/* Mobile nav toggle button - fixed position */}
      <button
        type="button"
        className="mobile-nav-toggle"
        onClick={() => setMobileNavOpen(true)}
        aria-label="Open course outline"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" />
          <line x1="3" y1="12" x2="3.01" y2="12" />
          <line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
      </button>

      {/* Mobile nav overlay */}
      {mobileNavOpen && (
        <div
          className="mobile-nav-overlay"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      {/* Mobile nav drawer */}
      <aside
        className={`mobile-nav-drawer ${mobileNavOpen ? 'open' : ''}`}
      >
        {/* Header */}
        <div style={{
          padding: '16px',
          borderBottom: '1px solid var(--border-subtle)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary)' }}>Course Outline</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={() => { setMobileNavOpen(false); setShareOpen(true); }}
            className="secondary-button"
            aria-label="Share course"
            title="Share course"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 8,
              padding: 0,
              flexShrink: 0
            }}
          >
            <Share2 size={16} />
          </button>
          <button
            type="button"
            onClick={() => setMobileNavOpen(false)}
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: 'var(--bg-tertiary)',
              border: 'none',
              color: 'var(--text-primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            aria-label="Close outline"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
          </div>
        </div>
        {/* Scrollable content — shares CourseOutlineTree with the desktop
            rail (see CourseOutlineTree.jsx). No sectionButtonRefs: the
            mobile drawer doesn't need scroll restoration since it closes
            on selection. */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', paddingBottom: 100 }}>
          <CourseOutlineTree
            outline={outline}
            loadingOutline={loadingOutline}
            selectedSectionId={selectedSectionId}
            localViewed={localViewed}
            currentLessonId={lessonView?.lesson?.lessonId}
            onSectionClick={handleMobileSectionClick}
          />
        </div>
      </aside>
      {/* Collapsed state: show thin rail with expand button (desktop only) */}
      {isCollapsed && (
        <div className="collapsed-rail-desktop" style={{
          width: 48,
          height: '100%',
          backgroundColor: 'var(--bg-elevated)',
          borderRight: '1px solid var(--border-subtle)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: 12,
          flexShrink: 0
        }}>
          <button type="button"
            onClick={() => setIsCollapsed(false)}
            className="secondary-button"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 8,
              padding: 0
            }}
            aria-label="Open outline"
            title="Open outline"
          >
            {/* Chevron right icon */}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}
      <div style={{ display: 'contents' }}>
        {/* Outline rail (desktop only) */}
        <aside
          className="learn-rail learn-rail-desktop"
          data-scrollbar="dark"
          style={{
            height: '100%',
            maxHeight: '100%',
            display: isCollapsed ? 'none' : 'flex',
            flexDirection: 'column',
            minWidth: 0,
            minHeight: 0,
            margin: 0,
            // One step lighter than the reading surface in both themes
            // (mockup 1c: rail #FAF9F6 against page #F7F6F3).
            backgroundColor: 'var(--bg-elevated)',
            borderRight: '1px solid var(--border-subtle)',
            boxSizing: 'border-box',
            overflow: 'hidden'
          }}
        >
          <CourseOutlineRail
            title={lessonTitle}
            outline={outline}
            loadingOutline={loadingOutline}
            selectedSectionId={selectedSectionId}
            localViewed={localViewed}
            currentLessonId={lessonView?.lesson?.lessonId}
            onSectionClick={handleRailSectionClick}
            sectionButtonRefs={sectionButtonRefs}
            railRef={railRef}
            onCollapse={() => setIsCollapsed(true)}
            progress={progress}
          >
            {/* V2 Adaptive Learning - Course Mastery Progress */}
            {v2Enabled && courseMastery && (
              <div style={{ marginBottom: 16 }}>
                <CourseMasteryProgress
                  mastery={courseMastery.mastery}
                  masteryLevel={courseMastery.masteryLevel}
                  confidence={courseMastery.confidence}
                  dueForReview={courseMastery.dueForReview}
                />
              </div>
            )}
          </CourseOutlineRail>
        </aside>

        {/* Section body + sticky footer, stacked in a flex column so the
            footer never scrolls out of view while the content above it
            does. This wrapper is the actual grid item (via the
            display:contents parent); .learn-body keeps its class/ref for
            newsletter-signup.css's scrollbar styling. */}
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0, minHeight: 0 }}>
        <main
          ref={bodyRef}
          className="learn-body"
          data-scrollbar="dark"
          style={{
            flex: '1 1 0',
            minHeight: 0,
            minWidth: 0,
            overflowY: 'auto', // Always auto - same for quiz and markdown
            overflowX: 'hidden',
            padding: (effectiveContentType === 'QUIZ' || effectiveContentType === 'KNOWLEDGE_CHECK') ? 0 : '50px 150px',
            margin: 0,
            backgroundColor: 'var(--bg-primary)',
            boxSizing: 'border-box',
            position: 'relative'
          }}
        >
          <div
            className="content-fade"
            style={{
              transition: 'opacity .25s ease, transform .25s ease',
              opacity: loadingLesson ? 0.4 : 1,
              transform: loadingLesson ? 'translateY(2px)' : 'translateY(0)',
              willChange: 'opacity, transform',
              ...((effectiveContentType === 'QUIZ' || effectiveContentType === 'KNOWLEDGE_CHECK')
                ? {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                  }
                : {}),
            }}
          >
            {status && <p className="hint" aria-live="polite" style={{ margin: '8px 12px' }}>{status}</p>}
            {ownershipWarning && (
              <div className="hint" style={{ margin: '8px 12px', padding: '8px 10px', border: '1px solid var(--border-subtle)', borderRadius: 8 }}>
                {ownershipWarning}
              </div>
            )}

            {loadingLesson && (
              /* Sits on the same --frame-prose measure as the real lesson so
                 the reading column doesn't jump when the content lands. */
              <SkeletonRegion
                label="Loading lesson"
                style={{ maxWidth: 'var(--frame-prose)', padding: '44px 40px 40px' }}
              >
                <Skeleton width="34%" height={12} style={{ marginBottom: 'var(--space-3)' }} />
                <Skeleton width="80%" height={34} style={{ marginBottom: 'var(--space-5)' }} />
                <SkeletonText lines={4} style={{ marginBottom: 'var(--space-5)' }} />
                <SkeletonText lines={3} lastLineWidth="48%" style={{ marginBottom: 'var(--space-5)' }} />
                <SkeletonText lines={4} lastLineWidth="70%" />
              </SkeletonRegion>
            )}
            {!loadingLesson && !renderLessonView && <p className="hint">Select a section on the left to begin.</p>}

            {!loadingLesson && renderLessonView && (() => {
              const active = renderLessonView?.sections?.find(s => (s?.section?.sectionId || s?.sectionId) === selectedSectionId) || renderLessonView?.sections?.[0];

              if (effectiveContentType === 'QUIZ') {
                return (
                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                    }}
                  >
                    <QuizView
                      isLoading={quizState.isLoading}
                      isSubmitting={quizState.isSubmitting}
                      error={quizState.error}
                      question={quizState.question}
                      difficulty={quizState.difficulty}
                      messages={quizState.messages}
                      streamingReply={quizState.streamingReply}
                      isStreaming={quizState.isStreaming}
                      isPassed={quizState.isPassed}
                      score={quizState.score}
                      inputValue={quizState.inputValue}
                      setInputValue={quizState.setInputValue}
                      submitAnswer={quizState.submitAnswer}
                      retry={quizState.retry}
                      chatEndRef={quizState.chatEndRef}
                      onSelectNavNext={onSelectNavNext}
                      hasNextSection={!!nextSectionInfo}
                    />
                  </div>
                );
              }

              if (effectiveContentType === 'KNOWLEDGE_CHECK') {
                return (
                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                    }}
                  >
                    <KnowledgeCheckView
                      status={knowledgeCheckState?.status}
                      error={knowledgeCheckState?.error}
                      isLoading={knowledgeCheckState?.isLoading}
                      isSubmitting={knowledgeCheckState?.isSubmitting}
                      questions={knowledgeCheckState?.questions || []}
                      previousAttempt={knowledgeCheckState?.previousAttempt}
                      answers={knowledgeCheckState?.answers || []}
                      setAnswer={knowledgeCheckState?.setAnswer}
                      allAnswered={knowledgeCheckState?.allAnswered}
                      results={knowledgeCheckState?.results}
                      score={knowledgeCheckState?.score}
                      total={knowledgeCheckState?.total}
                      passed={knowledgeCheckState?.passed}
                      initialize={knowledgeCheckState?.initialize}
                      submit={knowledgeCheckState?.submit}
                      retry={knowledgeCheckState?.retry}
                      onContinue={(didPass) => {
                        // Mark as locally viewed only if passed (backend confirmed)
                        if (didPass && onKnowledgeCheckComplete) {
                          onKnowledgeCheckComplete(selectedSectionId);
                        }
                        if (nextSectionInfo) {
                          onClickLessonSection(
                            nextSectionInfo.lessonId,
                            nextSectionInfo.sectionId,
                            lessonView?.lesson?.lessonId
                          );
                        }
                      }}
                    />
                  </div>
                );
              }

              if (effectiveContentType === 'HTML') {
                return (
                  <div ref={contentRef} className="lesson-content-selectable" style={{ position: 'relative' }}>
                    {HighlightOverlay}
                    <SavedHighlightsWrapper history={askGraspdHistory} containerRef={contentRef}>
                      <Suspense fallback={<p className="hint">Loading content...</p>}>
                        <HtmlView html={active?.content?.html || ''} />
                      </Suspense>
                    </SavedHighlightsWrapper>
                    {selection && courseId && lessonId && selectedSectionId && (
                      <AskGraspdPopup
                        selection={selection}
                        courseId={courseId}
                        lessonId={lessonId}
                        sectionId={selectedSectionId}
                        onClose={clearSelection}
                        onSuccess={addAskGraspdToHistory}
                      />
                    )}
                  </div>
                );
              }

              // Check for MARKDOWN content type from multiple sources
              // Normalize to uppercase for consistent comparison
              const rawContentType = active?.content?.type || active?.section?.contentType || effectiveContentType;
              const contentType = rawContentType ? rawContentType.toUpperCase() : null;
              if (contentType === 'MARKDOWN' || active?.content?.markdown) {
                return (
                  <div ref={contentRef} className="lesson-content-selectable" style={{ position: 'relative' }}>
                    {HighlightOverlay}
                    <SavedHighlightsWrapper history={askGraspdHistory} containerRef={contentRef}>
                      <MarkdownView markdown={active?.content?.markdown || ''} />
                    </SavedHighlightsWrapper>
                    {selection && courseId && lessonId && selectedSectionId && (
                      <AskGraspdPopup
                        selection={selection}
                        courseId={courseId}
                        lessonId={lessonId}
                        sectionId={selectedSectionId}
                        onClose={clearSelection}
                        onSuccess={addAskGraspdToHistory}
                      />
                    )}
                  </div>
                );
              }

              // Show loading state when:
              // 1. No active section found
              // 2. Active section has no content yet
              // 3. Content type is not yet determined (still resolving)
              if (!active || !active.content || !effectiveContentType) {
                return <p className="hint">Loading content...</p>;
              }

              // If we reach here, we have content but it's an unrecognized type
              // This should rarely happen - log for debugging
              if (process.env.NODE_ENV !== 'production') {
                console.warn('[CourseLearnView] Unrecognized content type:', {
                  effectiveContentType,
                  contentType,
                  activeContentType: active?.content?.type,
                  sectionContentType: active?.section?.contentType,
                });
              }

              return <p className="hint">Unsupported content type.</p>;
            })()}
          </div>
          {loadingLesson && (
            <div className="lesson-loading-scrim" />
          )}
        </main>
        {/* Sticky footer (mockup 1c, lines 696-707): progress + counts on
            the left, Previous + the primary completion action on the
            right. Completion state logic (four states) moved here verbatim
            from the old inline "Mark Completed" button — see LessonFooter.jsx. */}
        <LessonFooter
          percentComplete={progress.percentComplete}
          currentIndex={progress.currentIndex}
          totalSections={progress.totalSections}
          totalQuizzes={progress.totalQuizzes}
          quizzesPassed={progress.quizzesPassed}
          onPrev={onSelectNavPrev}
          prevDisabled={!hasPrev}
          showPrimaryAction={effectiveContentType !== 'QUIZ' && effectiveContentType !== 'KNOWLEDGE_CHECK' && hasValidContent}
          primaryLabel={displayViewed ? (nextSectionInfo ? 'Next' : 'Completed') : (markingComplete ? 'Marking…' : 'Mark lesson complete')}
          primaryDisabled={!!markingComplete || (displayViewed && !nextSectionInfo)}
          primaryBusy={!!markingComplete}
          primaryShowCheck={displayViewed}
          primaryTitle={displayViewed ? (nextSectionInfo ? 'Go to next section' : 'Course completed') : 'Mark lesson complete'}
          onPrimaryAction={() => {
            if (displayViewed) {
              // Already completed - navigate to next section
              if (nextSectionInfo) {
                onClickLessonSection(
                  nextSectionInfo.lessonId,
                  nextSectionInfo.sectionId,
                  lessonView?.lesson?.lessonId
                );
              }
            } else if (!markingComplete) {
              // Mark as completed and auto-navigate to next section
              onCompleteSection && onCompleteSection(nextSectionInfo);
            }
          }}
        />
        </div>
      </div>
    </div>
    </>
  );
}